import Layout from '@components/Layout'
import { useAuth } from '@context/AuthContext'
import { usePageTracking } from '@hooks/usePageTracking'
import AccommodationDetailPage from '@pages/Accommodations/AccommodationDetailPage'
import AdminPage, { AdminRoute } from '@pages/Admin'
import AccommodationsPage from '@pages/Accommodations/AccommodationsPage'
import BalancesPage from '@pages/Balances/BalancesPage'
import FilesPage from '@pages/Files'
import FlightDetailPage from '@pages/Flights/FlightDetailPage'
import FlightsPage from '@pages/Flights/FlightsPage'
import IdeasPage from '@pages/Ideas'
import HomePage from '@pages/Home'
import ItineraryImportPage from '@pages/ItineraryImport'
import ItineraryPage from '@pages/Itinerary/ItineraryPage'
import TripMapPage from '@pages/TripMap/TripMapPage'
import ProfilePage from '@pages/Profile'
import PublicItineraryPage from '@pages/PublicItinerary/PublicItineraryPage'
import TripsPage from '@pages/Trips'
import MembersPage from '@pages/Trips/MembersPage'
import TripDetailPage from '@pages/Trips/TripDetailPage'
import React from 'react'
import { Toaster } from 'react-hot-toast'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import styled from 'styled-components'
import { LoadingScreen } from './App.styles'
import LoginPage from './pages/Login'
import { GlobalStyle } from './styles/GlobalStyle'

const AppContainer = styled.div`
	height: 100%;
	background: #f8f9fa;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { token, isLoading } = useAuth()

	// Show loading while checking authentication
	if (isLoading) {
		return (
			<LoadingScreen>
				<div>Loading...</div>
			</LoadingScreen>
		)
	}

	if (!token) {
		return <Navigate to="/" replace />
	}

	return <Layout>{children}</Layout>
}

const RouteTracker: React.FC = () => {
	usePageTracking()
	return null
}

const App: React.FC = () => {
	const { token } = useAuth()

	return (
		<AppContainer>
			<GlobalStyle />
			<Toaster
				position="bottom-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: '#363636',
						color: '#fff',
					},
					success: {
						duration: 3000,
						iconTheme: {
							primary: '#4ade80',
							secondary: '#fff',
						},
					},
					error: {
						duration: 4000,
						iconTheme: {
							primary: '#ef4444',
							secondary: '#fff',
						},
					},
				}}
			/>
			<Router>
				<RouteTracker />
				<Routes>
					{/* Public Routes */}
					<Route path="/" element={token ? <Navigate to="/home" replace /> : <LoginPage />} />
					<Route path="/shared/trips/:tripCode/itinerary" element={<PublicItineraryPage />} />

					{/* Protected Routes */}
					<Route
						path="/home"
						element={
							<ProtectedRoute>
								<HomePage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/profile"
						element={
							<ProtectedRoute>
								<ProfilePage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips"
						element={
							<ProtectedRoute>
								<TripsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId"
						element={
							<ProtectedRoute>
								<TripDetailPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/members"
						element={
							<ProtectedRoute>
								<MembersPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/itinerary"
						element={
							<ProtectedRoute>
								<ItineraryPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/import"
						element={
							<ProtectedRoute>
								<ItineraryImportPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/map"
						element={
							<ProtectedRoute>
								<TripMapPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/accommodations"
						element={
							<ProtectedRoute>
								<AccommodationsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/accommodations/:accommodationId"
						element={
							<ProtectedRoute>
								<AccommodationDetailPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/flights"
						element={
							<ProtectedRoute>
								<FlightsPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/flights/:flightGroupId"
						element={
							<ProtectedRoute>
								<FlightDetailPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/ideas"
						element={
							<ProtectedRoute>
								<IdeasPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/files"
						element={
							<ProtectedRoute>
								<FilesPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/trips/:tripId/balances"
						element={
							<ProtectedRoute>
								<BalancesPage />
							</ProtectedRoute>
						}
					/>

						{/* Admin Routes (role-gated, own layout) */}
						<Route
							path="/admin/*"
							element={
								<AdminRoute>
									<AdminPage />
								</AdminRoute>
							}
						/>

						{/* Catch all route */}
						<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</Router>
		</AppContainer>
	)
}

export default App
